import { useEffect, useState } from "react";
import { Loader2, FileText, AlertTriangle, Send, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui-kit/Dialog";
import { Button } from "@/components/ui-kit/Button";
import { StatusBadge } from "@/components/ui-kit/StatusBadge";
import { InvoiceBreakdown, fromDraft } from "./InvoiceBreakdown";
import { useFunder } from "@/funder/FunderContext";

/**
 * Deliberate review-and-confirm step before an invoice goes out. Shows the
 * computed to-date draft for a payer, warns on supersede, and lets Linda either
 * Issue (freeze + send) or hold it as a Draft.
 */
export function InvoiceReviewDialog({ open, onOpenChange, matterId, payer, onDone }) {
  const { payerLabel } = useFunder();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !payer) return;
    setDraft(null);
    setError("");
    api.get(`/matters/${matterId}/invoice-draft?payer=${payer}`).then((r) => setDraft(r.data)).catch(() => {});
  }, [open, payer, matterId]);

  const act = async (asDraft) => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/matters/${matterId}/invoices`, { payer, draft: asDraft });
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const willSupersede = draft?.will_supersede || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="invoice-review-dialog">
        <DialogTitle>Review {payerLabel(payer)} invoice</DialogTitle>
        <DialogDescription>
          Computed to date from the billing engine — figures are not hand-entered. Review before it goes out.
        </DialogDescription>

        {!draft ? (
          <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-3 py-2">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground">
                <FileText className="h-4 w-4 text-primary" /> {draft.next_number}
              </span>
              <span className="text-xs text-muted-foreground">{payerLabel(payer)} · {draft.matter_name}</span>
            </div>

            <InvoiceBreakdown payer={payer} data={fromDraft(draft.breakdown)} />

            {willSupersede.length > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-status-superseded/30 bg-status-superseded/10 px-3 py-2" data-testid="supersede-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-superseded" />
                <p className="text-sm text-foreground">
                  Issuing will supersede{" "}
                  {willSupersede.map((w, i) => (
                    <span key={w.id}>
                      {i > 0 && ", "}
                      <strong>{w.number}</strong> <StatusBadge status={w.status} className="ml-1" />
                    </span>
                  ))}. The prior invoice will be marked Superseded.
                </p>
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => act(true)} disabled={busy} data-testid="save-draft-button">
                <Save className="h-4 w-4" /> Save as Draft
              </Button>
              <Button onClick={() => act(false)} disabled={busy} data-testid="issue-invoice-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Issue {draft.next_number}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
